import React, { useState, useEffect, useCallback } from 'react';
import API from '../utils/api';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Modal, ConfirmDialog, PageLoader, EmptyState, Badge, ActionBtn, SectionHeader, FormField, StatCard, Pagination } from '../components/common/UI';
import { toShamsi, formatCurrency } from '../utils/dateHelpers';
import { useAuth } from '../context/AuthContext';
import ShamsiDatePicker from '../components/common/ShamsiDatePicker';
import { 
  FaMoneyBillWave, 
  FaCreditCard, 
  FaChartLine, 
  FaHandshake,
  FaProjectDiagram,
  FaChalkboardTeacher,
  FaCalendarAlt,
  FaBoxes,
  FaUndo,
} from 'react-icons/fa';
import { MdRefresh } from '../utils/icons';
import { MdPersonRemove, MdHistory, MdEdit, MdDelete } from 'react-icons/md';

export default function Finance() {
  const { isManager } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [sponsorships, setSponsorships] = useState([]);
  const [classes, setClasses] = useState([]);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [equipment, setEquipment] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawForm, setWithdrawForm] = useState({ amount: '', partnerName: 'دفتر', reason: '', description: '', date: '' });
  const [totalWithdrawn, setTotalWithdrawn] = useState(0);
  const [rentalIncome, setRentalIncome] = useState(0);
  const [returnWithdrawal, setReturnWithdrawal] = useState(null);
  const [returnAmount, setReturnAmount] = useState('');
  const [withdrawalsPage, setWithdrawalsPage] = useState(1); // پیج‌بندی (صفحه‌بندی سمت کلاینت)
  const [editWithdrawId, setEditWithdrawId] = useState(null);
  const [showEditWithdrawModal, setShowEditWithdrawModal] = useState(false);
  const [deleteWithdrawId, setDeleteWithdrawId] = useState(null);
  
  const [summary, setSummary] = useState({
    projectIncome: 0,
    projectExpense: 0,
    projectProfit: 0,
    sponsorshipIncome: 0,
    sponsorshipExpense: 0,
    sponsorshipProfit: 0,
    classIncome: 0,
    classExpense: 0,
    classProfit: 0,
    totalIncome: 0,
    totalExpense: 0,
    totalProfit: 0,
    totalReceivable: 0,
    equipmentCost: 0,
    maintenanceCost: 0,
    finalProfit: 0,
    expensesTotal: 0,
    expensesCount: 0,
    officeDebtsPaid: 0,
    rentalIncome: 0,
    rentalCount: 0,
    projectReceivable: 0,
    sponsorshipReceivable: 0,
    classReceivable: 0,
  });

  const loadAllData = useCallback(async () => {
    try {
      setLoading(true);
      const [projRes, sponRes, classRes, equipRes, withdrawRes, expRes, rentalRes, officeDebtRes] = await Promise.all([
        API.get('/projects'),
        API.get('/sponsorships'),
        API.get('/classes'),
        API.get('/equipment'),
        API.get('/withdrawals'),
        API.get('/expenses'),
        API.get('/rental-equipment'),
        API.get('/office-debts'),
      ]);
      
      setProjects(projRes.data);
      setSponsorships(sponRes.data.data || []);
      setClasses(classRes.data.data || []);
      setEquipment(equipRes.data);

      const equipments = equipRes.data;
      const ownedEquipments = equipments.filter(e => !e.isRental); // تجهیزات ملکی
      const totalOwnedValue = ownedEquipments.reduce((sum, e) => sum + (e.remainingPrice || e.purchasePrice || 0), 0);
      const totalOwnedCount = ownedEquipments.length;
      const withdrawalsData = withdrawRes.data.data || [];
      const returnedAmount = withdrawalsData.filter(w => w.isReturned).reduce((sum, w) => sum + (w.returnedAmount || w.amount), 0);
      const totalWithdrawnAmount = withdrawalsData.reduce((sum, w) => sum + (w.amount || 0), 0) - returnedAmount;
      setWithdrawals(withdrawalsData);
      setTotalWithdrawn(totalWithdrawnAmount);
      

      const rentalTotal = rentalRes.data.total || 0;
      const rentalCount = rentalRes.data.data?.length || 0;
      setRentalIncome(rentalTotal);
      

      const totalExpensesAmount = expRes.data.total || 0;
      const expensesCount = expRes.data.data?.length || 0;
      
      // بدهکاری‌های دفتر: تا وقتی پرداخت نشده روی سود اثر نمی‌گذارد،
      // فقط مبلغی که واقعاً پرداخت شده (حتی جزئی) از سود کم می‌شود
      const officeDebtsData = officeDebtRes.data.data || [];
      const paidOfficeDebtsTotal = officeDebtsData.reduce((s, d) => s + (d.paidAmount || 0), 0);
      
      calculateProfits(projRes.data, sponRes.data.data || [], classRes.data.data || [], equipRes.data, totalWithdrawnAmount, totalExpensesAmount, expensesCount, rentalTotal, rentalCount, totalOwnedValue, totalOwnedCount, paidOfficeDebtsTotal);
      
    } catch (err) {
      toast.error('خطا در بارگذاری داده‌ها');
    } finally {
      setLoading(false);
    }
  }, []);

  const calculateProfits = (projectsData, sponsorshipsData, classesData, equipmentData, withdrawalsTotal = 0, expensesTotal = 0, expensesCount = 0, rentalTotal = 0, rentalCount = 0, totalOwnedValue = 0, totalOwnedCount = 0, paidOfficeDebtsTotal = 0) => {
    const startDate = dateRange.start ? new Date(dateRange.start) : new Date(2000, 0, 1);
    const endDate = dateRange.end ? new Date(dateRange.end) : new Date(Date.now() + 30*24*60*60*1000);
    
    // 1. پروژه‌ها
    let projectIncome = 0;
    let projectExpense = 0;
    let projectReceivable = 0;
    let projectRentalCost = 0;
    
    projectsData.forEach(p => {
      const projectDate = new Date(p.eventDate);
      if (projectDate >= startDate && projectDate <= endDate) {
        projectIncome += p.paidAmount || 0;
        projectReceivable += (p.totalAmount - p.paidAmount) || 0;
        
        if (p.assignedTeamWithSalary && p.assignedTeamWithSalary.length > 0) {
          p.assignedTeamWithSalary.forEach(emp => {
            projectExpense += emp.salary || 0;
          });
        }
        
        if (p.rentalEquipment && p.rentalEquipment.length > 0) {
          projectRentalCost += p.rentalEquipment.reduce((sum, item) => sum + (item.price || 0), 0);
        }
      }
    });
    
    // 2. اسپانسرشیپ‌ها
    let sponsorshipIncome = 0;
    let sponsorshipExpense = 0;
    let sponsorshipReceivable = 0;
    
    sponsorshipsData.forEach(s => {
      const sponsorDate = new Date(s.startDate);
      if (sponsorDate >= startDate && sponsorDate <= endDate) {
        sponsorshipIncome += s.paidAmount || 0;
        sponsorshipExpense += s.cost || 0;
        sponsorshipReceivable += (s.budget - (s.paidAmount || 0)) || 0;
      }
    });
    
    // 3. کلاس‌ها
    let classIncome = 0;
    let classExpense = 0;
    let classReceivable = 0;
    
    classesData.forEach(c => {
      const classDate = new Date(c.startDate);
      if (classDate >= startDate && classDate <= endDate) {
        classIncome += c.students?.reduce((sum, s) => sum + (s.paidAmount || 0), 0) || 0;
        const totalClassAmount = (c.price || 0) * (c.students?.length || 0);
        const totalPaid = c.students?.reduce((sum, s) => sum + (s.paidAmount || 0), 0) || 0;
        classReceivable += (totalClassAmount - totalPaid) || 0;
      }
    });
    
    // 4. تجهیزات ملکی (سرمایه اولیه)
    let equipmentCost = 0;
    let maintenanceCost = 0;
    
    equipmentData.forEach(e => {
      if (!e.isRental) {
        equipmentCost += e.purchasePrice || 0;
        if (e.status === 'Maintenance') {
          maintenanceCost += (e.purchasePrice || 0) * 0.1;
        }
      }
    });
    
    // جمع کل باقیات
    const totalReceivable = projectReceivable + sponsorshipReceivable + classReceivable;
    
    // کل درآمد و هزینه
    const totalIncome = projectIncome + sponsorshipIncome + classIncome + rentalTotal;
    const totalExpense = projectExpense + sponsorshipExpense + classExpense + expensesTotal + projectRentalCost + paidOfficeDebtsTotal;
    const totalProfit = totalIncome - totalExpense;
    
    setSummary({
      // پروژه‌ها
      projectIncome,
      projectExpense,
      projectProfit: projectIncome - projectExpense,
      projectRentalCost,
      projectReceivable,
      
      // اسپانسرشیپ
      sponsorshipIncome,
      sponsorshipExpense,
      sponsorshipProfit: sponsorshipIncome - sponsorshipExpense,
      sponsorshipReceivable,
      
      // کلاس‌ها
      classIncome,
      classExpense,
      classProfit: classIncome - classExpense,
      classReceivable,
      
      // کل مالی
      totalIncome,
      totalExpense,
      totalProfit,
      totalReceivable,
      
      // تجهیزات
      equipmentCost,
      maintenanceCost,
      totalEquipmentValue: totalOwnedValue,
      totalEquipmentCount: totalOwnedCount,
      
      // مصارفات
      expensesTotal,
      expensesCount,
      
      // بدهکاری‌های دفتر (فقط پرداخت‌شده‌ها)
      officeDebtsPaid: paidOfficeDebtsTotal,
      
      // برداشت
      withdrawals: withdrawalsTotal,
      finalProfit: totalProfit - withdrawalsTotal,
      
      // کرایه
      rentalIncome: rentalTotal,
      rentalCount,
    });
  };

  const handleReturnWithdrawal = async () => {
    if (!returnAmount || returnAmount <= 0) {
      return toast.error('مبلغ برگشتی را وارد کنید');
    }
    
    // چک اینکه از مبلغ برداشت شده بیشتر نباشد
    if (Number(returnAmount) > returnWithdrawal.amount) {
      return toast.error(`مبلغ برگشتی (${formatCurrency(returnAmount)}) از مبلغ برداشت (${formatCurrency(returnWithdrawal.amount)}) بیشتر است!`);
    }
    
    try {
      await API.put(`/withdrawals/${returnWithdrawal._id}/return`, {
        returnedAmount: Number(returnAmount)
      });
      toast.success('برداشت با موفقیت برگشت داده شد');
      setReturnWithdrawal(null);
      setReturnAmount('');
      loadAllData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطا در برگشت برداشت');
    }
  };
    

  const saveWithdrawal = async () => {
    if (!withdrawForm.amount || !withdrawForm.partnerName) {
      return toast.error('مبلغ و نام شریک ضروری است');
    }
    try {
      await API.post('/withdrawals', withdrawForm);
      toast.success('برداشت ثبت شد');
      setShowWithdrawModal(false);
      setWithdrawForm({ amount: '', partnerName: 'دفتر', reason: '', description: '', date: '' });
      loadAllData(); // رفرش صفحه
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطا در ثبت برداشت');
    }
  };

  const openEditWithdrawal = (w) => {
    setEditWithdrawId(w._id);
    setWithdrawForm({
      amount: w.amount,
      partnerName: w.partnerName,
      reason: w.reason || '',
      description: w.description || '',
      date: w.date ? w.date.split('T')[0] : '',
    });
    setShowEditWithdrawModal(true);
  };

  const updateWithdrawal = async () => {
    if (!withdrawForm.amount || !withdrawForm.partnerName) {
      return toast.error('مبلغ و نام شریک ضروری است');
    }
    try {
      await API.put(`/withdrawals/${editWithdrawId}`, withdrawForm);
      toast.success('برداشت بروزرسانی شد');
      setShowEditWithdrawModal(false);
      setEditWithdrawId(null);
      setWithdrawForm({ amount: '', partnerName: 'دفتر', reason: '', description: '', date: '' });
      loadAllData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطا در بروزرسانی برداشت');
    }
  };

  const deleteWithdrawal = async () => {
    try {
      await API.delete(`/withdrawals/${deleteWithdrawId}`);
      toast.success('برداشت حذف شد');
      setDeleteWithdrawId(null);
      loadAllData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطا در حذف برداشت');
    }
  };

  useEffect(() => {
    loadAllData();
  }, [dateRange]);

  useEffect(() => { setWithdrawalsPage(1); }, [dateRange]);

  if (loading) return <PageLoader />;

  const WITHDRAWALS_PAGE_SIZE = 10;
  const withdrawalsTotalPages = Math.max(1, Math.ceil(withdrawals.length / WITHDRAWALS_PAGE_SIZE));
  const pagedWithdrawals = withdrawals.slice((withdrawalsPage - 1) * WITHDRAWALS_PAGE_SIZE, withdrawalsPage * WITHDRAWALS_PAGE_SIZE);

  return (
    <div className="space-y-5 fade-in">
      <SectionHeader 
        title="داشبورد مالی" 
        subtitle="سود و زیان پروژه‌ها، اسپانسرشیپ‌ها و کلاس‌ها"
        actions={
          <div className="flex gap-2">
            <button onClick={() => setShowDateFilter(!showDateFilter)} className="p-2 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">
              <FaCalendarAlt size={16} className="text-gray-600" />
            </button>
            <button onClick={loadAllData} className="p-2 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">
              <MdRefresh size={18} className="text-gray-600" />
            </button>
          </div>
        }
      />

      {/* فیلتر تاریخ */}
      {showDateFilter && (
        <div className="glass-card p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className="text-xs text-gray-500 block mb-1">از تاریخ</label>
              <ShamsiDatePicker value={dateRange.start} onChange={date => setDateRange({ ...dateRange, start: date })} className="form-input w-full" />
            </div>
            <div className="flex-1">
              <label className="text-xs text-gray-500 block mb-1">تا تاریخ</label>
              <ShamsiDatePicker value={dateRange.end} onChange={date => setDateRange({ ...dateRange, end: date })} className="form-input w-full" />
            </div>
            <div className="flex items-end">
              <button onClick={() => setDateRange({ start: '', end: '' })} className="px-4 py-2 bg-red-100 text-red-600 rounded-xl text-sm">پاک کردن</button>
            </div>
          </div>
        </div>
      )}

      {/* کارت‌های اصلی */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard icon={<FaMoneyBillWave className="text-green-600 text-lg" />} label="درآمد کل" value={formatCurrency(summary.totalIncome)} color="green" onClick={() => navigate('/reports')} />
        <StatCard icon={<FaCreditCard className="text-red-600 text-lg" />} label="هزینه کل" value={formatCurrency(summary.totalExpense)} color="red" onClick={() => navigate('/daily-expenses')} />
        <StatCard icon={<FaChartLine className="text-blue-600 text-lg" />} label="سود خالص" value={formatCurrency(summary.totalProfit)} color={summary.totalProfit >= 0 ? 'green' : 'red'} onClick={() => navigate('/reports')} />
        <Link to="/debts" className="block">
  <div className="glass-card p-4 text-center border border-yellow-200 hover:border-yellow-200 transition-all">
    <div className="flex justify-center mb-2">
      <FaCreditCard className="text-yellow-600 text-2xl" />
    </div>
    <div className="text-2xl font-bold text-gray-800">{formatCurrency(summary.totalReceivable)}</div>
    <div className="text-xs text-gray-500 mt-1">باقیات کل</div>
    <div className="text-xs text-gray-600 mt-2">
      پروژه: {formatCurrency(summary.projectReceivable)} | اسپانسر: {formatCurrency(summary.sponsorshipReceivable)} | کلاس: {formatCurrency(summary.classReceivable)}
    </div>
  </div>
</Link>
        <StatCard icon={<FaBoxes className="text-blue-600 text-lg" />} label="سرمایه اولیه" value={formatCurrency(summary.totalEquipmentValue)} sub={`${summary.totalEquipmentCount} تجهیز`} color="blue" onClick={() => navigate('/equipment')} />
      </div>

      {/* کارت‌های تفکیکی پروژه، اسپانسرشیپ، کلاس */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* پروژه‌ها */}
        <div className="glass-card p-4 border border-blue-200">
  <div className="flex items-center gap-2 mb-3">
    <FaProjectDiagram className="text-blue-600" />
    <h3 className="text-gray-800 font-bold">پروژه‌ها</h3>
  </div>
  <div className="space-y-2">
    <div className="flex justify-between">
      <span className="text-gray-500 text-sm">درآمد:</span>
      <span className="text-green-600">{formatCurrency(summary.projectIncome)}</span>
    </div>
    <div className="flex justify-between">
      <span className="text-gray-500 text-sm">هزینه (دستمزد):</span>
      <span className="text-red-600">{formatCurrency(summary.projectExpense)}</span>
    </div>
    <div className="flex justify-between">
      <span className="text-gray-500 text-sm">هزینه تجهیزات کرایه‌ای:</span>
      <span className="text-red-600">{formatCurrency(summary.projectRentalCost)}</span>
    </div>
    <div className="flex justify-between pt-2 border-t border-gray-200">
      <span className="text-gray-800 font-bold">سود خالص:</span>
      <span className={`font-bold ${(summary.projectProfit - (summary.projectRentalCost || 0)) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
        {formatCurrency((summary.projectProfit || 0) - (summary.projectRentalCost || 0))}
      </span>
    </div>
  </div>
</div>

        {/* اسپانسرشیپ‌ها */}
        <div className="glass-card p-4 border border-purple-200">
          <div className="flex items-center gap-2 mb-3">
            <FaHandshake className="text-purple-600" />
            <h3 className="text-gray-800 font-bold">اسپانسرشیپ</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-500 text-sm">درآمد (بودجه):</span>
              <span className="text-green-600">{formatCurrency(summary.sponsorshipIncome)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 text-sm">هزینه تولید:</span>
              <span className="text-red-600">{formatCurrency(summary.sponsorshipExpense)}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-gray-200">
              <span className="text-gray-800 font-bold">سود خالص:</span>
              <span className={`font-bold ${summary.sponsorshipProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(summary.sponsorshipProfit)}
              </span>
            </div>
          </div>
        </div>

        {/* کلاس‌ها */}
        <div className="glass-card p-4 border border-yellow-200">
          <div className="flex items-center gap-2 mb-3">
            <FaChalkboardTeacher className="text-yellow-600" />
            <h3 className="text-gray-800 font-bold">کلاس‌ها</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-500 text-sm">درآمد (شهریه):</span>
              <span className="text-green-600">{formatCurrency(summary.classIncome)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 text-sm">هزینه کلاس:</span>
              <span className="text-red-600">{formatCurrency(summary.classExpense)}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-gray-200">
              <span className="text-gray-800 font-bold">سود خالص:</span>
              <span className={`font-bold ${summary.classProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(summary.classProfit)}
              </span>
            </div>
          </div>
        </div>

        {/* تجهیزات کرایی */}
<div className="glass-card p-4 border border-blue-200">
  <div className="flex items-center gap-2 mb-3">
    <FaBoxes className="text-blue-600" />
    <h3 className="text-gray-800 font-bold">تجهیزات کرایی</h3>
  </div>
  <div className="space-y-2">
    <div className="flex justify-between">
      <span className="text-gray-500 text-sm">درآمد حاصل از کرایه:</span>
      <span className="text-green-600">{formatCurrency(summary.rentalIncome || 0)}</span>
    </div>
    <div className="flex justify-between">
      <span className="text-gray-500 text-sm">تعداد تجهیزات:</span>
      <span className="text-gray-800">{summary.rentalCount || 0}</span>
    </div>
    <Link to="/rental-equipment" className="block w-full mt-2 py-1.5 text-center text-xs bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors">
      مدیریت تجهیزات کرایی
    </Link>
  </div>
</div>

{/* مصارفات روزانه */}
<div className="glass-card p-4 border border-red-200">
  <div className="flex items-center gap-2 mb-3">
    <FaMoneyBillWave className="text-red-600" />
    <h3 className="text-gray-800 font-bold">مصارفات روزانه</h3>
  </div>
  <div className="space-y-2">
    <div className="flex justify-between">
      <span className="text-gray-500 text-sm">کل مصارفات:</span>
      <span className="text-red-600">{formatCurrency(summary.expensesTotal || 0)}</span>
    </div>
    <div className="flex justify-between">
      <span className="text-gray-500 text-sm">تعداد مصارفات:</span>
      <span className="text-gray-800">{summary.expensesCount || 0}</span>
    </div>
    <Link to="/daily-expenses" className="block w-full mt-2 py-1.5 text-center text-xs bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors">
      مشاهده جزئیات
    </Link>
  </div>
</div>

{/* بدهکاری‌های دفتر */}
<div className="glass-card p-4 border border-red-200">
  <div className="flex items-center gap-2 mb-3">
    <FaMoneyBillWave className="text-red-600" />
    <h3 className="text-gray-800 font-bold">بدهکاری‌های دفتر (پرداخت‌شده)</h3>
  </div>
  <div className="space-y-2">
    <div className="flex justify-between">
      <span className="text-gray-500 text-sm">کسر شده از سود خالص:</span>
      <span className="text-red-600">{formatCurrency(summary.officeDebtsPaid || 0)}</span>
    </div>
    <p className="text-xs text-gray-500">بدهکاری‌های پرداخت‌نشده روی سود اثر ندارند</p>
    <Link to="/office-debts" className="block w-full mt-2 py-1.5 text-center text-xs bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors">
      مشاهده جزئیات
    </Link>
  </div>
</div>
      </div>

      {/* برداشت شرکا */}
<div className="glass-card p-4 border border-orange-200">
  <div className="flex items-center gap-2 mb-3">
    <MdPersonRemove className="text-orange-600" size={20} />
    <h3 className="text-gray-800 font-bold">برداشت شرکا</h3>
  </div>
  <div className="space-y-2">
    <div className="flex justify-between">
      <span className="text-gray-500 text-sm">کل برداشت شده:</span>
      <span className="text-orange-600">{formatCurrency(totalWithdrawn)}</span>
    </div>
    <div className="flex justify-between">
      <span className="text-gray-500 text-sm">سود خالص شرکت:</span>
      <span className="text-green-600">{formatCurrency(summary.totalProfit)}</span>
    </div>
    <div className="flex justify-between pt-2 border-t border-gray-200">
      <span className="text-gray-800 font-bold">سود قابل تقسیم:</span>
      <span className={`font-bold ${summary.finalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
        {formatCurrency(summary.finalProfit)}
      </span>
    </div>
    {isManager && (
      <button onClick={() => setShowWithdrawModal(true)} className="w-full mt-2 py-1.5 text-xs bg-orange-100 text-orange-600 rounded-lg hover:bg-orange-200 transition-colors">
        + ثبت برداشت جدید
      </button>
    )}
  </div>
</div>

{/* لیست تاریخچه برداشت‌ها */}
{withdrawals.length > 0 && (
  <div className="glass-card overflow-hidden">
    <div className="p-4 border-b border-gray-200 flex justify-between items-center">
      <h3 className="text-gray-800 font-bold flex items-center gap-2">
        <MdHistory size={18} className="text-orange-600" />
        تاریخچه برداشت‌های شرکا
      </h3>
      <span className="text-sm text-gray-500">
        مجموع برداشت: {formatCurrency(totalWithdrawn)}
      </span>
    </div>
    <div className="overflow-x-auto">
      <table className="premium-table">
        <thead>
          <tr>
            <th>تاریخ</th><th>شریک</th><th>مبلغ</th><th>دلیل</th><th>توضیحات</th><th>عملیات</th>
          </tr>
        </thead>
        <tbody>
          {pagedWithdrawals.map(w => (
            <tr key={w._id}>
              <td className="text-gray-600 text-sm">{toShamsi(w.date)}</td>
              <td className="text-gray-800">{w.partnerName}</td>
              <td className="text-orange-600 font-bold">{formatCurrency(w.amount)}</td>
              <td className="text-gray-500 text-sm">{w.reason || '---'}</td>
              <td className="text-gray-500 text-sm">{w.description || '---'}</td>
              <td>
                <div className="flex items-center gap-1">
                  {!w.isReturned ? (
                    <button
                      onClick={() => setReturnWithdrawal(w)}
                      className="p-1.5 text-green-600 hover:text-green-700 hover:bg-green-100 rounded-lg transition-colors"
                      title="برگشت برداشت"
                    >
                      <FaUndo size={14} />
                    </button>
                  ) : (
                    <span className="text-xs text-green-600">برگشت داده شده</span>
                  )}
                  {isManager && (
                    <button
                      onClick={() => openEditWithdrawal(w)}
                      className="p-1.5 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-100 rounded-lg transition-colors"
                      title="ویرایش برداشت"
                    >
                      <MdEdit size={15} />
                    </button>
                  )}
                  {isManager && (
                    <button
                      onClick={() => setDeleteWithdrawId(w._id)}
                      className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-100 rounded-lg transition-colors"
                      title="حذف برداشت"
                    >
                      <MdDelete size={15} />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot className="border-t border-gray-200">
          <tr>
            <td colSpan="2" className="py-2 text-gray-800 font-bold">جمع کل</td>
            <td className="text-orange-600 font-bold">{formatCurrency(totalWithdrawn)}</td>
            <td colSpan="3"></td>
          </tr>
        </tfoot>
      </table>
    </div>
    <div className="px-4">
      <Pagination page={withdrawalsPage} totalPages={withdrawalsTotalPages} onChange={setWithdrawalsPage} />
    </div>
  </div>
)}

{/* مودال برداشت */}
<Modal isOpen={showWithdrawModal} onClose={() => setShowWithdrawModal(false)} title="برداشت جدید" size="sm">
  <div className="space-y-4">
    <FormField label="نام شریک" required>
      <select className="form-input" value={withdrawForm.partnerName} onChange={e => setWithdrawForm({...withdrawForm, partnerName: e.target.value})}>
        <option value="دفتر">دفتر</option>
        <option value="جعفری">جعفری</option>
        <option value="ادیب">ادیب</option>
        <option value="نجیب">نجیب</option>
      </select>
    </FormField>
    <FormField label="مبلغ برداشت (افغانی)" required>
      <input type="number" className="form-input" value={withdrawForm.amount} onChange={e => setWithdrawForm({...withdrawForm, amount: e.target.value})} placeholder="0" />
    </FormField>
    <FormField label="تاریخ برداشت">
    <ShamsiDatePicker
      value={withdrawForm.date}
      onChange={(date) => setWithdrawForm({ ...withdrawForm, date: date })}
      placeholder="تاریخ برداشت"
      className="form-input"
    />
    </FormField>
    <FormField label="دلیل برداشت">
      <input className="form-input" value={withdrawForm.reason} onChange={e => setWithdrawForm({...withdrawForm, reason: e.target.value})} placeholder="مثال: برداشت سود ماهانه" />
    </FormField>
    <FormField label="توضیحات">
      <textarea className="form-input h-16 resize-none" value={withdrawForm.description} onChange={e => setWithdrawForm({...withdrawForm, description: e.target.value})} placeholder="توضیحات..." />
    </FormField>
  </div>
  <div className="flex gap-3 justify-end mt-5">
    <button onClick={() => setShowWithdrawModal(false)} className="px-4 py-2 rounded-xl bg-gray-100 text-gray-800 text-sm">انصراف</button>
    <ActionBtn onClick={saveWithdrawal} label="ثبت برداشت" />
  </div>
</Modal>

{returnWithdrawal && (
  <Modal isOpen={!!returnWithdrawal} onClose={() => setReturnWithdrawal(null)} title="برگشت برداشت" size="sm">
    <div className="space-y-4">
      <div>شریک: {returnWithdrawal.partnerName}</div>
      <div>مبلغ برداشت: {formatCurrency(returnWithdrawal.amount)}</div>
      <input 
        type="number" 
        className="form-input" 
        placeholder="مبلغ برگشتی" 
        value={returnAmount} 
        onChange={e => setReturnAmount(e.target.value)}
        max={returnWithdrawal.amount}
      />
      <div className="flex gap-3 justify-end">
        <button onClick={() => setReturnWithdrawal(null)} className="px-4 py-2 bg-gray-100 rounded-lg">انصراف</button>
        <button onClick={handleReturnWithdrawal} className="px-4 py-2 bg-yellow-600 rounded-lg">تأیید برگشت</button>
      </div>
    </div>
  </Modal>
)}

{/* مودال ویرایش برداشت */}
<Modal isOpen={showEditWithdrawModal} onClose={() => { setShowEditWithdrawModal(false); setEditWithdrawId(null); }} title="ویرایش برداشت" size="sm">
  <div className="space-y-4">
    <FormField label="نام شریک" required>
      <select className="form-input" value={withdrawForm.partnerName} onChange={e => setWithdrawForm({...withdrawForm, partnerName: e.target.value})}>
        <option value="دفتر">دفتر</option>
        <option value="جعفری">جعفری</option>
        <option value="ادیب">ادیب</option>
        <option value="نجیب">نجیب</option>
      </select>
    </FormField>
    <FormField label="مبلغ برداشت (افغانی)" required>
      <input type="number" className="form-input" value={withdrawForm.amount} onChange={e => setWithdrawForm({...withdrawForm, amount: e.target.value})} placeholder="0" />
    </FormField>
    <FormField label="تاریخ برداشت">
    <ShamsiDatePicker
      value={withdrawForm.date}
      onChange={(date) => setWithdrawForm({ ...withdrawForm, date: date })}
      placeholder="تاریخ برداشت"
      className="form-input"
    />
    </FormField>
    <FormField label="دلیل برداشت">
      <input className="form-input" value={withdrawForm.reason} onChange={e => setWithdrawForm({...withdrawForm, reason: e.target.value})} placeholder="مثال: برداشت سود ماهانه" />
    </FormField>
    <FormField label="توضیحات">
      <textarea className="form-input h-16 resize-none" value={withdrawForm.description} onChange={e => setWithdrawForm({...withdrawForm, description: e.target.value})} placeholder="توضیحات..." />
    </FormField>
  </div>
  <div className="flex gap-3 justify-end mt-5">
    <button onClick={() => { setShowEditWithdrawModal(false); setEditWithdrawId(null); }} className="px-4 py-2 rounded-xl bg-gray-100 text-gray-800 text-sm">انصراف</button>
    <ActionBtn onClick={updateWithdrawal} label="بروزرسانی" />
  </div>
</Modal>

<ConfirmDialog
  isOpen={!!deleteWithdrawId}
  onClose={() => setDeleteWithdrawId(null)}
  onConfirm={deleteWithdrawal}
  title="حذف برداشت"
  message="آیا مطمئن هستید که می‌خواهید این برداشت را حذف کنید؟"
/>

      

      {/* لیست باقیات */}
      <div className="glass-card overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-gray-800 font-bold">لیست بدهکاران (باقیات)</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="premium-table">
            <thead>
              <tr>
                <th>نام پروژه/اسپانسر</th><th>مبلغ کل</th><th>پرداخت شده</th><th>باقیات</th>
              </tr>
            </thead>
            <tbody>
              {projects.filter(p => (p.totalAmount - p.paidAmount) > 0).map(p => {
                const remaining = p.totalAmount - p.paidAmount;
                return (
                  <tr key={p._id}>
                    <td className="font-semibold text-gray-800">{p.name} (پروژه)</td>
                    <td className="text-green-600">{formatCurrency(p.totalAmount)}</td>
                    <td className="text-blue-600">{formatCurrency(p.paidAmount)}</td>
                    <td className="text-red-600 font-bold">{formatCurrency(remaining)}</td>
                  </tr>
                );
              })}
              {sponsorships.filter(s => ((s.budget || 0) - (s.paidAmount || 0)) > 0).map(s => {
                const remaining = (s.budget || 0) - (s.paidAmount || 0);
                return (
                  <tr key={s._id}>
                    <td className="font-semibold text-gray-800">{s.title} (اسپانسر)</td>
                    <td className="text-green-600">{formatCurrency(s.budget)}</td>
                    <td className="text-blue-600">{formatCurrency(s.paidAmount || 0)}</td>
                    <td className="text-red-600 font-bold">{formatCurrency(remaining)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}